import { css, cva } from '@styled-system/css';
import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

export type InputSize = 'sm' | 'md' | 'lg';
export type InputState = 'default' | 'error';

const wrapper = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3',
    width: 'full',
    bg: 'surface.default',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.strong',
    borderRadius: 'md',
    color: 'text.primary',
    transition: 'border-color 120ms ease, box-shadow 120ms ease, background 120ms ease',
    cursor: 'text',
    _focusWithin: {
      borderColor: 'border.focus',
      boxShadow: '0 0 0 3px token(colors.brand.primarySoft)',
    },
  },
  variants: {
    size: {
      sm: { h: '26px', px: '5', fontSize: 'caption' },
      md: { h: '32px', px: '6', fontSize: 'bodySm' },
      lg: { h: '38px', px: '7', fontSize: 'body' },
    },
    state: {
      default: {},
      error: {
        borderColor: 'error.default',
        _focusWithin: {
          borderColor: 'error.default',
          boxShadow: '0 0 0 3px token(colors.error.soft)',
        },
      },
    },
    disabled: {
      true: {
        bg: 'surface.sunken',
        color: 'text.tertiary',
        cursor: 'not-allowed',
        opacity: 0.7,
        _focusWithin: { borderColor: 'border.strong', boxShadow: 'none' },
      },
      false: {},
    },
  },
  defaultVariants: { size: 'md', state: 'default', disabled: false },
});

const inputEl = css({
  flex: 1,
  minWidth: 0,
  bg: 'transparent',
  border: 'none',
  outline: 'none',
  fontFamily: 'sans',
  fontSize: 'inherit',
  color: 'inherit',
  _placeholder: { color: 'text.tertiary' },
  _disabled: { cursor: 'not-allowed' },
});

const adornment = css({
  display: 'inline-flex',
  alignItems: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
});

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  inputSize?: InputSize;
  state?: InputState;
  leading?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ inputSize = 'md', state = 'default', leading, trailing, className, disabled, ...props }, ref) => {
    return (
      <div className={`${wrapper({ size: inputSize, state, disabled: disabled ? true : false })} ${className ?? ''}`}>
        {leading && <span className={adornment}>{leading}</span>}
        <input ref={ref} className={inputEl} disabled={disabled} {...props} />
        {trailing && <span className={adornment}>{trailing}</span>}
      </div>
    );
  },
);

Input.displayName = 'Input';
